import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpService } from '../shared/services/http.service';

@Component({
    selector: 'app-client-detail',
    templateUrl: './client-detail.component.html',
    styleUrls: ['./client-detail.component.scss'],
    standalone: false
})
export class ClientDetailComponent implements OnInit, OnChanges {

  @Input() client;

  @Output() close: EventEmitter<Boolean> = new EventEmitter<Boolean>();

  submitted = false

  kinds = ["EDUCATION","LAW","FINANCE","OTHER"]

  schoolLevel = ["PRIMARY", "SECONDARY", "BOTH"]

  parentClients : any = []

  timeout = null

  showAdvanced = false

  clientForm: UntypedFormGroup


  allTags: {id: string, tag: string}[] = []

  currentTags: {id: string | null, tag: string}[] = []


  tagInput = ''
  tagsChanged = false

  constructor(private fb: UntypedFormBuilder, private httpService: HttpService ) { }

  ngOnInit() {

    if( !this.clientForm ){
      this.createClientForm()
    }

  }

  get f() {
    return this.clientForm.controls
  }

  get isEducationSelected(): boolean {
    return this.clientForm.get('kind').value === 'EDUCATION';
  }

  get canCreateNewTag(): boolean {
    const tag = this.tagInput.trim()
    if (!tag) return false
    const lower = tag.toLowerCase()
    return !this.currentTags.some(t => t.tag.toLowerCase() === lower)
      && !this.allTags.some(t => t.tag.toLowerCase() === lower)
  }

  get filteredAllTags(): {id: string, tag: string}[] {
    if (!this.tagInput) return this.allTags
    return this.allTags.filter(t => t.tag.toLowerCase().includes(this.tagInput.toLowerCase()))
  }

  isTagApplied(tag: {id: string, tag: string}): boolean {
    return this.currentTags.some(ct => ct.tag === tag.tag)
  }

  toggleTag(tag: {id: string, tag: string}) {
    const applied = this.currentTags.find(ct => ct.tag === tag.tag)
    if (applied) {
      this.removeTag(applied)
    } else {
      this.addTag(tag)
    }
  }

  ngOnChanges(){
    this.createClientForm()
    this.rebuildForm()
    this.tagsChanged = false
    this.currentTags = []
    this.tagInput = ''
    this.loadTags()
  }

  async searchForParentClients( searchTerm ){

    if( this.timeout ){
      window.clearTimeout( this.timeout )
      this.timeout = null
    }

    if( !searchTerm || searchTerm.length < 3 ){
      this.parentClients = []
      return
    }

    this.timeout = window.setTimeout( async () => {

      const kind = this.clientForm.get('kind').value

      const resp : any = await this.httpService.postAsync("hclient/searchForParents", {kind, searchTerm, id: this.client.id})

      if( resp.success ){
        this.parentClients = resp.clients;
      }
    }, 250)


  }

  async loadTags() {
    const allResp: any = await this.httpService.postAsync('htag/list', {})
    if (allResp.success){
      this.allTags = (allResp.data || []).map((t: any) => ({id: t.id, tag: t.tag}))
    }

    console.log('Loaded all tags %o', this.allTags )

    if (this.client?.tags) {
      const tagNames = this.client.tags.split(';').map((s: string) => s.trim()).filter(Boolean)
      this.currentTags = tagNames.map((tag: string) => {
        const match = this.allTags.find(t => t.tag === tag)
        return match ? {id: match.id, tag: match.tag} : {id: null, tag:tag}
      })
    }
  }

  removeTag(tag: {id: string | null, tag: string}) {
    this.currentTags = this.currentTags.filter(t => t !== tag)
    this.tagsChanged = true
  }

  addTag(tag: {id: string, tag: string}) {
    this.currentTags = [...this.currentTags, tag]
    this.tagInput = ''
    this.tagsChanged = true
  }

  addNewTag() {
    const tag = this.tagInput.trim()
    if (!tag) return
    const existing = this.allTags.find(t => t.tag.toLowerCase() === tag.toLowerCase())
    if (existing) {
      this.addTag(existing)
      return
    }
    this.currentTags = [...this.currentTags, {id: null, tag:tag}]
    this.tagInput = ''
    this.tagsChanged = true
  }

  async saveTags(clientId: string) {
    if (!clientId || !this.tagsChanged) return

    const originalTagNames = (this.client.tags?.split(';') || []).map((s: string) => s.trim()).filter(Boolean)

    for (const tag of this.currentTags) {
      if (originalTagNames.includes(tag.tag)) continue

      let tagId = this.allTags.find(t => t.tag === tag.tag)?.id
      if (!tagId) {
        const resp: any = await this.httpService.postAsync('htag/create', {tag: tag.tag})
        if (!resp.success) continue
        tagId = resp.id
      }
      await this.httpService.postAsync('htag/assign', {tagId, clientIds: [clientId]})
    }

    for (const tag of originalTagNames) {
      if (this.currentTags.some(ct => ct.tag === tag)) continue
      const tagId = this.allTags.find(t => t.tag === tag)?.id
      if (tagId){
        await this.httpService.postAsync('htag/unassign', {tagId, clientIds: [clientId]})
      }else{
        console.log("Coudln't find tag id for tag %o to remove.", tag, this.allTags)
      }
    }
  }

  rebuildForm(){
    if( !this.client ){
      return
    }
    this.clientForm.reset({
      contract_start: this.convertDate(this.client.contract_start),
      name: this.client.name,
      contract_end: this.convertDate(this.client.contract_end),
      kind: this.client.kind,
      emails: this.client.emails,
      school_level: this.client.school_level,
      parent_client: null,
      search_term: "",
      oauthurl:"",
      username:"",
      clientid:"",
      password:"",
      secret:"",
      salt:"",
    })

    if( this.client.parent_id ){
      const selectedParentClient = { id: this.client.parent_id, name: this.client.parent_name };
      this.parentClients = [selectedParentClient]
      this.clientForm.patchValue({
        parent_client: this.client.parent_id,
      });

      // Apply the visible text after the conditional input has rendered.
      window.setTimeout(() => {
        this.clientForm.patchValue({
          search_term: this.client.parent_name
        }, { emitEvent: false });
      });
    }
    this.submitted = false

  }

  displayParentClient(client: any): string {
    if (typeof client === 'string') {
      return client;
    }

    return client && client.name ? client.name : '';
  }

  onParentSelected(event: any): void {
    const selectedClient = event.option.value;
    this.clientForm.patchValue({
      parent_client: selectedClient.id,
      search_term: selectedClient.name
    });
  }

  onCancel(){
    this.close.emit(false)
  }

  async onSubmit(){
    const formModel = this.clientForm.value;
    this.submitted = true

    if( this.clientForm.invalid ){
      return
    }

    const save = {
      id: this.client && this.client.id ,
      name: formModel.name,
      contract_start: formModel.contract_start,
      contract_end: formModel.contract_end,
      kind: formModel.kind,
      school_level: formModel.school_level,
      emails: formModel.emails,
      parent_client_id: formModel.parent_client,
      oauthurl: formModel.oauthurl,
      username: formModel.username,
      password: formModel.password,
      clientid: formModel.clientid,
      secret: formModel.secret,
      salt: formModel.salt,

    }

    let result
    if( save.id ){
      result = await this.httpService.postAsync( 'hclient/updateClient', save )
    }
    if( !save.id ){
      result = await this.httpService.postAsync( 'hclient/newClient', save )
    }
    if( !result.success ){
      alert("Error saving.")
    }

    if( result.success ){
      const clientId = save.id || (result as any)?.clientId
      await this.saveTags(clientId)
    }

    this.close.emit(true)
  }

  convertDate( dateString ){

    if( !dateString ){
      return null
    }
    var dateParts = dateString.split("-")


    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
  }

  createClientForm(){
    this.clientForm = this.fb.group({
      name: [this.client.name, Validators.required],
      contract_start: [ this.convertDate(this.client.contract_start), Validators.compose([Validators.required])],
      contract_end: [ this.convertDate( this.client.contract_end), Validators.compose([Validators.required])],
      kind: [this.client.kind || 'EDUCATION',  Validators.required],
      school_level: [this.client.school_level],
      emails: [this.client.emails],
      parent_client: [this.client.parent_id],
      search_term: [""],
      oauthurl: [this.client.oauthurl],
      username: [this.client.username],
      password: [this.client.password],
      clientid: [this.client.clientid],
      secret: [this.client.secret],
      salt: [this.client.salt],

    })

    this.clientForm.get('kind').valueChanges.subscribe(val => {
      if (val === 'EDUCATION') {
        this.clientForm.get('school_level').setValidators([Validators.required]);
      } else {
        this.clientForm.get('school_level').clearValidators();
      }
      this.clientForm.get('school_level').updateValueAndValidity();
    });

    // Trigger initial check
    this.clientForm.get('kind').updateValueAndValidity({onlySelf: false, emitEvent: true});
  }

  async archive(){
    const confirmed = confirm("Are you sure you want to archive this client?")
    if( confirmed ){
      const resp:any = await this.httpService.postAsync( 'hclient/archive', {id: this.client.id} )
      if( !resp.success ){
        alert("Couldn't archive client. Message from server: " + resp.message )
        return
      }
      this.close.emit(true)
    }
  }

  async delete(){

    const tag = prompt("Enter client tag exactly to confirm deleting all data for this client")

    if( tag == this.client.tag){
      const resp:any = await this.httpService.postAsync( 'hclient/delete', {id: this.client.id} )
      if( !resp.success ){
        alert("Couldn't delete client. Message from server: " + resp.message )
        return
      }
      this.close.emit(true)
    }
  }

  async fetchOAuthDetails(){

    const resp: any = await this.httpService.postAsync( 'hclient/fetchOAuthSecret', {clientId: this.client.id} )

    if( resp.success ){
      this.showAdvanced = true
      this.clientForm.get('oauthurl').setValue( resp.secret.oauthurl )
      this.clientForm.get('username').setValue( resp.secret.username )
      this.clientForm.get('password').setValue( resp.secret.password )
      this.clientForm.get('clientid').setValue( resp.secret.clientid )
      this.clientForm.get('secret').setValue( resp.secret.secret )
      this.clientForm.get('salt').setValue( resp.secret.salt )
    }
    return
  }

  async saveOAuthDetails(){

    const oauthurl = this.clientForm.get('oauthurl').value
    const username = this.clientForm.get('username').value
    const secret = this.clientForm.get('secret').value
    const salt = this.clientForm.get('salt').value
    const clientid = this.clientForm.get('clientid').value
    const password = this.clientForm.get('password').value


    const resp: any = await this.httpService.postAsync( 'hclient/saveOAuthSecret', {clientId: this.client.id,
      secret: {oauthurl, username, password, clientid, secret, salt}
    } )

    if( resp.success ){
      this.showAdvanced = false
    }

    return
  }

}
